import React from 'react';
import type { PatientStaffNote } from '../../../patients/types';
import { PatientProfileSectionCard } from '../PatientProfileSectionCard';

export interface PatientStaffNotesCardProps {
  notes: PatientStaffNote[];
}

/**
 * Staff notes, read from the PMS.
 *
 * Staff already write notes in their PMS — giving them a second place to write
 * them splits the record and guarantees the two disagree. This card mirrors
 * what's in the PMS so the front desk can read it without switching systems,
 * and is deliberately read-only: no input, no save, no edit.
 *
 * No author line: `patient.PatientNote` carries no author column on any of the
 * three PMS sync paths, so attributing a note would mean inventing the name.
 * Date only.
 */
export function PatientStaffNotesCard({ notes }: PatientStaffNotesCardProps) {
  return (
    <PatientProfileSectionCard
      icon="doc"
      title={`Staff notes · ${String(notes.length)}`}
      accent="var(--ink-3)"
      subtitle="From your PMS"
    >
      {notes.length === 0 ? (
        <p className="m-0 text-[13px] text-[var(--ink-3)]">No notes recorded in the PMS</p>
      ) : (
        <div className="flex flex-col">
          {notes.map((n, i) => (
            <div
              key={n.id}
              className="py-[11px]"
              style={{ borderTop: i > 0 ? '1px solid var(--cream-2)' : 'none' }}
            >
              <div className="text-[11.5px] text-[var(--ink-4)]">{n.at}</div>
              <p className="m-0 mt-1 text-[13px] leading-[1.55] text-[var(--ink-1)]">{n.text}</p>
            </div>
          ))}
        </div>
      )}
    </PatientProfileSectionCard>
  );
}
